/**
 * UnitsStep - Step 1 of Add Budget Dialog: Select kostenplaatsen
 */

import React from "react";
import { Cube, MagnifyingGlass, Spinner } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UnitDTO } from "shared/types/unit";

interface UnitsStepProps {
  unitIds: string[];
  setUnitIds: (ids: string[]) => void;
  buildingUnits: UnitDTO[];
  loadingUnits: boolean;
  unitSearch: string;
  setUnitSearch: (value: string) => void;
  onConfirm: () => void;
}

export const UnitsStep: React.FC<UnitsStepProps> = ({
  unitIds,
  setUnitIds,
  buildingUnits,
  loadingUnits,
  unitSearch,
  setUnitSearch,
  onConfirm,
}) => {
  const filteredUnits = React.useMemo(() => {
    if (!unitSearch.trim()) return buildingUnits.filter((u) => u.id);
    const search = unitSearch.toLowerCase().trim();
    return buildingUnits.filter((unit) => unit.id && unit.id.toLowerCase().includes(search));
  }, [buildingUnits, unitSearch]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>
          Kostenplaatsen * <span className="text-xs text-neutral-400">(meerdere mogelijk)</span>
        </Label>
        {!loadingUnits && filteredUnits.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setUnitIds(filteredUnits.map((u) => u.id!))}
              className="h-7 px-2 text-xs"
            >
              Selecteer alles
            </Button>
            {unitIds.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setUnitIds([])}
                className="h-7 px-2 text-xs text-red-600"
              >
                Deselecteer alles
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <Input
          type="text"
          value={unitSearch}
          onChange={(e) => setUnitSearch(e.target.value)}
          placeholder="Zoek kostenplaats..."
          className="pl-9"
          autoFocus
        />
      </div>

      {loadingUnits ? (
        <div className="flex items-center gap-2 text-neutral-500">
          <Spinner className="h-4 w-4 animate-spin" />
          Laden...
        </div>
      ) : filteredUnits.length === 0 ? (
        <div className="py-4 text-center text-neutral-500">
          <Cube className="mx-auto mb-2 h-8 w-8 opacity-50" />
          <p>
            {unitSearch
              ? `Geen kostenplaatsen gevonden voor "${unitSearch}"`
              : "Geen kostenplaatsen gevonden"}
          </p>
        </div>
      ) : (
        <>
          <div className="grid max-h-48 grid-cols-2 gap-2 overflow-y-auto">
            {filteredUnits.map((unit) => {
              const isSelected = unitIds.includes(unit.id!);
              return (
                <Button
                  key={unit.id}
                  type="button"
                  variant={isSelected ? "default" : "outline"}
                  className={`justify-start ${isSelected ? "bg-moss-primary" : ""}`}
                  onClick={() => {
                    setUnitIds(
                      isSelected
                        ? unitIds.filter((id) => id !== unit.id)
                        : [...unitIds, unit.id!]
                    );
                  }}
                >
                  <Cube className="mr-2 h-4 w-4" />
                  {unit.id}
                </Button>
              );
            })}
          </div>
          {unitIds.length > 0 && (
            <div className="space-y-2 border-t pt-2">
              <div className="text-xs text-neutral-500">
                {unitIds.length} kostenplaats(en) geselecteerd
              </div>
              <Button
                type="button"
                onClick={onConfirm}
                className="w-full bg-moss-primary hover:bg-moss-primary/90"
              >
                Bekijk beschikbare budgetten
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UnitsStep;
